import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [pageAccess, setPageAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get('/permissions/me');
      setPermissions(data.permissions || {});
      setPageAccess(data.pages || null);
    } catch {
      setPermissions({});
      setPageAccess(null);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          // Fetch permissions after auth
          const { data } = await api.get('/permissions/me');
          setPermissions(data.permissions || {});
          setPageAccess(data.pages || null);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Fetch permissions after login
    try {
      const { data } = await api.get('/permissions/me');
      setPermissions(data.permissions || {});
      setPageAccess(data.pages || null);
    } catch {
      setPermissions({});
      setPageAccess(null);
    }
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions(null);
    setPageAccess(null);
  };

  /**
   * Check if the current user has a specific permission.
   * @param {string} section - e.g. "customers", "admin"
   * @param {string} action - e.g. "view", "edit", "delete"
   * @returns {boolean}
   */
  const hasPermission = useCallback((section, action = 'view') => {
    if (!permissions) return true; // Still loading, allow
    if (user?.role === 'superuser') return true; // Superuser bypasses all
    const sectionPerms = permissions[section];
    if (!sectionPerms) return false;
    return sectionPerms[action] === true;
  }, [permissions, user]);

  /**
   * Check if the current user can access a specific sidebar page.
   * Defaults to true while the page-access map is still loading or when
   * the route isn't registered (e.g. customer detail `/customers/:id` —
   * those inherit from the `/customers` list page).
   */
  const canAccessPage = useCallback((path) => {
    if (user?.role === 'superuser') return true;
    if (!pageAccess) return true; // still loading — don't flicker the sidebar
    if (!(path in pageAccess)) return true; // unregistered route — allow
    return pageAccess[path] !== false;
  }, [pageAccess, user]);

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    permissions,
    pageAccess,
    hasPermission,
    canAccessPage,
    refreshPermissions: fetchPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
